/**
 * KAPE Course Editor · Teaching Team panel
 * Embedded near the top of the Course Development Blueprint (intake) stage.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { KCIS_ADMIN_PALETTE as PAL } from "./KcisAdminShell";

export default function TeachingTeamPanel({ courseId, intake }) {
  const [rows, setRows] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [roles, setRoles] = useState([]);
  const [add, setAdd] = useState({ instructor_id: "", role: "Primary Instructor" });
  const [suggestions, setSuggestions] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/kcis/admin/kape/courses/${courseId}/teaching-assignments`);
      setRows(data.items || []);
      setRoles(data.roles || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/kcis/admin/instructor-profiles");
        setProfiles((data.items || []).filter((p) => p.active !== false));
      } catch { /* ignore */ }
    })();
  }, []);

  const runSuggestions = async () => {
    try {
      const params = new URLSearchParams();
      if (intake?.category) params.set("college", intake.category);
      if (intake?.program) params.set("program", intake.program);
      const kw = [intake?.title, intake?.audience].filter(Boolean).join(",");
      if (kw) params.set("expertise", kw);
      const { data } = await api.get(`/kcis/admin/university/instructor-suggestions?${params}`);
      setSuggestions(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Suggestion lookup failed"); }
  };

  const addAssignment = async () => {
    if (!add.instructor_id) { toast.error("Pick an instructor"); return; }
    try {
      await api.post(`/kcis/admin/kape/courses/${courseId}/teaching-assignments`, add);
      toast.success("Added to teaching team");
      setAdd({ instructor_id: "", role: "Primary Instructor" });
      setSuggestions([]);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  const removeAssignment = async (id) => {
    if (!window.confirm("Remove from teaching team?")) return;
    try {
      await api.delete(`/kcis/admin/kape/courses/${courseId}/teaching-assignments/${id}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  return (
    <div className="rounded-2xl p-5 border" style={{ borderColor: PAL.border, background: PAL.panel }}
         data-testid="teaching-team-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>Teaching Team</div>
          <div className="text-sm mt-1" style={{ color: PAL.muted }}>
            Course-level roles. Do not use appointments to grant access to individual courses.
          </div>
        </div>
        <button onClick={runSuggestions}
                className="text-xs rounded-full px-3 py-1"
                style={{ background: PAL.accent, color: "#0F0B14" }}
                data-testid="tt-suggest">
          Suggest instructors
        </button>
      </div>

      {rows.length === 0 && (
        <div className="text-xs" style={{ color: PAL.muted }} data-testid="tt-empty">
          No teaching team yet. Add a Primary Instructor to enable generation.
        </div>
      )}

      <div className="space-y-1">
        {rows.map((r) => {
          const prof = profiles.find((p) => p.id === r.instructor_id);
          const name = prof?.identity?.full_name || r.instructor_id.slice(0, 8);
          return (
            <div key={r.id} className="flex items-center gap-2 text-sm"
                 data-testid={`tt-row-${r.id}`}>
              <span className="font-medium">{name}</span>
              <span className="text-xs px-2 py-0.5 rounded-full"
                    style={{ background: PAL.accentSoft, color: PAL.text }}>{r.role}</span>
              <button onClick={() => removeAssignment(r.id)}
                      className="ml-auto text-[11px] rounded-full px-2 py-0.5 border"
                      style={{ borderColor: PAL.border, color: "#F87171" }}
                      data-testid={`tt-remove-${r.id}`}>Remove</button>
            </div>
          );
        })}
      </div>

      {suggestions.length > 0 && (
        <div className="mt-3 rounded p-2"
             style={{ background: "#0F0B14", border: `1px solid ${PAL.border}` }}
             data-testid="tt-suggestions">
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: PAL.muted }}>
            Suggested (score-ranked)
          </div>
          {suggestions.map((s) => (
            <button key={s.instructor_id}
                    onClick={() => setAdd((a) => ({ ...a, instructor_id: s.instructor_id }))}
                    className="w-full flex items-center gap-2 py-1 text-left hover:opacity-80"
                    data-testid={`tt-suggestion-${s.instructor_id}`}>
              <span className="text-xs font-medium">{s.full_name || s.instructor_id.slice(0, 8)}</span>
              <span className="text-[10px]" style={{ color: PAL.muted }}>score {s.score}</span>
              {s.appointment_types.length ? (
                <span className="text-[10px]" style={{ color: PAL.accent }}>· {s.appointment_types.join(", ")}</span>
              ) : null}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <select value={add.instructor_id}
                onChange={(e) => setAdd((a) => ({ ...a, instructor_id: e.target.value }))}
                className="rounded p-2 text-xs bg-transparent border flex-1 min-w-[200px]"
                style={{ borderColor: PAL.border, color: PAL.text }}
                data-testid="tt-add-instructor">
          <option value="">Choose instructor…</option>
          {profiles.map((p) => (
            <option key={p.id} value={p.id}>{p?.identity?.full_name || p.slug}</option>
          ))}
        </select>
        <select value={add.role}
                onChange={(e) => setAdd((a) => ({ ...a, role: e.target.value }))}
                className="rounded p-2 text-xs bg-transparent border"
                style={{ borderColor: PAL.border, color: PAL.text }}
                data-testid="tt-add-role">
          {roles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={addAssignment}
                disabled={!add.instructor_id}
                className="text-xs rounded-full px-3 py-1 disabled:opacity-40"
                style={{ background: PAL.accent, color: "#0F0B14" }}
                data-testid="tt-add-submit">
          Add
        </button>
      </div>
    </div>
  );
}
